import React, { useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useEditorImageImport } from '@/hooks/editor/useEditorImageImport';

interface ImportImageContentProps {
  workbookId?: string;
  onImageImport: (imageUrl: string, altText?: string) => void;
  accentColor?: string;
  className?: string;
}

interface ImportImageItem {
  id: string;
  url: string;
  alt_text?: string;
  filename?: string;
  width?: number;
  height?: number;
  user_id?: string;
  workbook_id?: string;
}

const ImportImageCard: React.FC<{
  image: ImportImageItem;
  onSelect: () => void;
  index: number;
}> = ({ image, onSelect, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.02 }}
      className="relative group cursor-pointer rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md"
      onClick={onSelect}
    >
      {/* Image - Square aspect ratio */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-800">
        <img 
          src={image.url} 
          alt={image.alt_text || 'Image'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      

    </motion.div>
  );
};

export const ImportImageContent: React.FC<ImportImageContentProps> = ({
  workbookId,
  onImageImport,
  accentColor = 'blue',
  className
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Load images with workbook context
  const {
    data: allImages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount
  } = useEditorImageImport({
    workbookId,
    searchQuery: debouncedSearch
  });

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className={cn("h-[400px] flex flex-col w-full max-w-full overflow-hidden", className)}>
      {/* Search Bar - Fixed at top */}
      <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50 flex-shrink-0 w-full">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={16} />
          <Input
            placeholder="Search your accessible images..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 relative z-0 w-full"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>


      
      {/* Scrollable Content Area - PROVEN PATTERN FROM SIDEBAR */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && allImages.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading images...</span>
          </div>
        ) : allImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {searchQuery ? 'No images found' : 'No images yet'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Create some images or get editor access to projects to see them here'
              }
            </p>
          </div>
        ) : (
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {allImages.map((image, index) => (
              <ImportImageCard
                key={image.id}
                image={image}
                index={index}
                onSelect={() => onImageImport(image.url, image.alt_text)}
              />
            ))}
            
            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex items-center justify-center py-4 col-span-full">
                {isFetchingNextPage ? (
                  <>
                    <Loader2 size={16} className="animate-spin text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Loading more...</span>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Load More
                  </Button>
                )}
              </div>
            )}
            
            {/* End of Results */}
            {!hasNextPage && allImages.length > 0 && (
              <div className="flex items-center justify-center py-4 col-span-full">
                <span className="text-xs text-gray-400">
                  {allImages.length} of {totalCount} images
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 